/**
 * Notes and attachments API.
 * Attachments are linked to any entity via entityId + entityName.
 */

import { apiFetch, apiFormFetch } from './fetch';
import { API } from '../constants';

// ─── Notes ───────────────────────────────────────────────────────────────────

export interface NoteResponse {
    id: string;
    entityId: string;
    entity: string;
    notes: string;
    isActive: boolean;
    createdAt: string;
    createdBy: string;
}

export async function listNotesByEntity(entity: string, entityId: string): Promise<NoteResponse[]> {
    const result = await apiFetch<NoteResponse[] | null>(
        `${API.NOTES}/${encodeURIComponent(entity)}/${encodeURIComponent(entityId)}`,
    );
    return Array.isArray(result) ? result : [];
}

export async function createNote(entityId: string, entity: string, notes: string): Promise<NoteResponse> {
    return apiFetch<NoteResponse>(API.NOTES, {
        method: 'POST',
        body: JSON.stringify({ entityId, entity, notes }),
    });
}

export async function updateNote(noteId: string, notes: string): Promise<NoteResponse> {
    return apiFetch<NoteResponse>(`${API.NOTES}/${noteId}`, {
        method: 'PUT',
        body: JSON.stringify({ notes }),
    });
}

export async function deleteNote(noteId: string): Promise<void> {
    await apiFetch(`${API.NOTES}/${noteId}`, { method: 'DELETE' });
}

// ─── Attachments ─────────────────────────────────────────────────────────────

export interface AttachmentResponse {
    id: string;
    entityId: string;
    entityName: string;
    fileName: string;
    attachmentKey: string;
    contentType: string;
    fileSize: number;
    isActive: boolean;
    createdAt: string;
    createdBy: string;
    modifiedAt?: string;
    modifiedBy?: string;
    downloadUrl?: string;
}

/** List attachments by entity. */
export async function listAttachmentsByEntity(entityId: string, entityName = 'note'): Promise<AttachmentResponse[]> {
    if (!entityId) {
        return [];
    }
    const result = await apiFetch<AttachmentResponse[] | null>(
        `${API.ATTACHMENTS}/entity/${encodeURIComponent(entityId)}?entityName=${encodeURIComponent(entityName)}`,
    );
    return Array.isArray(result) ? result : [];
}

/**
 * Fetch a short-lived download URL for an attachment. The server derives the
 * S3 key from the attachment record; the client never names one (DUBVM-124).
 */
export async function getPresignedDownloadUrl(attachmentId: string): Promise<string> {
    if (!attachmentId) {
        return '';
    }
    const resp = await apiFetch<{ url: string; key: string }>(API.S3_PRESIGN_DOWNLOAD, {
        method: 'POST',
        body: JSON.stringify({ attachmentId }),
    });
    return resp.url;
}

export async function fetchAttachmentsWithUrls(
    entityId: string,
    entityName = 'note',
): Promise<(AttachmentResponse & { downloadUrl: string })[]> {
    if (!entityId) {
        return [];
    }
    const attachments = await listAttachmentsByEntity(entityId, entityName);
    if (attachments.length === 0) {
        return [];
    }

    const results = await Promise.allSettled(
        attachments.map(async (att) => {
            let downloadUrl = '';
            try {
                downloadUrl = att.id ? await getPresignedDownloadUrl(att.id) : '';
            } catch {
                // failed to get download URL
            }
            return { ...att, downloadUrl };
        }),
    );
    return results
        .filter(
            (r): r is PromiseFulfilledResult<AttachmentResponse & { downloadUrl: string }> => r.status === 'fulfilled',
        )
        .map((r) => r.value);
}

export async function deleteAttachment(id: string): Promise<void> {
    await apiFetch(`${API.ATTACHMENTS}/${id}`, { method: 'DELETE' });
}

/**
 * Upload a file as an attachment linked to an entity.
 * Backend extracts createdBy from the bearer token.
 * @param file - The file to upload
 * @param entityId - Primary key of the parent entity (e.g. noteId, quoteId)
 * @param entityName - Table name of the parent entity (e.g. 'note', 'quotes')
 */
export async function uploadAttachment(file: File, entityId: string, entityName = 'note'): Promise<AttachmentResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entityId', entityId);
    formData.append('entityName', entityName);

    return apiFormFetch<AttachmentResponse>(API.ATTACHMENTS, formData);
}
